import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "../../api";
import { toast } from "react-toastify";
import { FaTrash, FaPlus, FaCheck, FaTimes } from "react-icons/fa";

const EMPTY = { label: "", unit: "forfait", unit_price: "" };

export default function PresetLinesTab() {
  const { t } = useTranslation();
  const [lines, setLines]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding]   = useState(false);
  const [draft, setDraft]     = useState(EMPTY);
  const [editId, setEditId]   = useState(null);
  const [editDraft, setEditDraft] = useState(EMPTY);

  const fetch = async () => {
    try {
      const { data } = await api.get("/settings/preset-lines");
      setLines(data);
    } catch { toast.error(t("settings.presets.loadError")); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const create = async () => {
    if (!draft.label.trim()) return;
    try {
      await api.post("/settings/preset-lines", { ...draft, unit_price: parseFloat(draft.unit_price) || 0 });
      setDraft(EMPTY); setAdding(false);
      await fetch();
      toast.success(t("settings.presets.added"));
    } catch { toast.error(t("settings.presets.addError")); }
  };

  const startEdit = (line) => { setEditId(line.id); setEditDraft({ label: line.label, unit: line.unit, unit_price: String(line.unit_price) }); };
  const cancelEdit = () => { setEditId(null); setEditDraft(EMPTY); };

  const update = async (id) => {
    try {
      await api.put(`/settings/preset-lines/${id}`, { ...editDraft, unit_price: parseFloat(editDraft.unit_price) || 0 });
      cancelEdit(); await fetch();
      toast.success(t("settings.presets.updated"));
    } catch { toast.error(t("settings.presets.updateError")); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/settings/preset-lines/${id}`);
      await fetch();
      toast.success(t("settings.presets.deleted"));
    } catch { toast.error(t("settings.presets.deleteError")); }
  };

  const EditInput = ({ field, value, onChange, type = "text", placeholder = "" }) => (
    <input type={type} value={value} onChange={(e) => onChange(field, e.target.value)} placeholder={placeholder}
      className="input input-bordered input-xs bg-base-200 border-base-300 w-full"
    />
  );

  if (loading) return <div className="loading loading-spinner" />;

  return (
    <div className="space-y-4">
      <p className="text-sm text-base-content/60">
        {t("settings.presets.intro")}
      </p>

      <div className="overflow-x-auto">
        <table className="table table-sm w-full">
          <thead>
            <tr className="text-base-content/60 text-xs uppercase">
              <th>{t("settings.presets.colLabel")}</th>
              <th className="w-32">{t("settings.presets.colUnit")}</th>
              <th className="w-32 text-right">{t("settings.presets.colPrice")}</th>
              <th className="w-20" />
            </tr>
          </thead>
          <tbody>
            {lines.map((line) =>
              editId === line.id ? (
                <tr key={line.id} className="bg-base-200">
                  <td><EditInput field="label"      value={editDraft.label}      onChange={(f,v) => setEditDraft(d=>({...d,[f]:v}))} placeholder={t("settings.presets.phLabel")} /></td>
                  <td><EditInput field="unit"       value={editDraft.unit}       onChange={(f,v) => setEditDraft(d=>({...d,[f]:v}))} placeholder="forfait" /></td>
                  <td><EditInput field="unit_price" value={editDraft.unit_price} onChange={(f,v) => setEditDraft(d=>({...d,[f]:v}))} type="number" placeholder="0" /></td>
                  <td>
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => update(line.id)} className="btn btn-ghost btn-xs text-success"><FaCheck /></button>
                      <button onClick={cancelEdit}            className="btn btn-ghost btn-xs text-error"><FaTimes /></button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={line.id} className="hover cursor-pointer" onClick={() => startEdit(line)}>
                  <td className="font-medium">{line.label}</td>
                  <td className="text-base-content/60">{line.unit}</td>
                  <td className="text-right font-mono">{Number(line.unit_price).toFixed(2)}</td>
                  <td>
                    <button onClick={(e) => { e.stopPropagation(); remove(line.id); }}
                      className="btn btn-ghost btn-xs text-error float-right">
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              )
            )}

            {adding && (
              <tr className="bg-base-200">
                <td><input type="text" value={draft.label} onChange={(e) => setDraft(d=>({...d,label:e.target.value}))}
                  placeholder={t("settings.presets.phLabel")} autoFocus
                  className="input input-bordered input-xs bg-base-100 border-base-300 w-full"
                /></td>
                <td><input type="text" value={draft.unit} onChange={(e) => setDraft(d=>({...d,unit:e.target.value}))}
                  placeholder="forfait"
                  className="input input-bordered input-xs bg-base-100 border-base-300 w-full"
                /></td>
                <td><input type="number" value={draft.unit_price} onChange={(e) => setDraft(d=>({...d,unit_price:e.target.value}))}
                  placeholder="0"
                  className="input input-bordered input-xs bg-base-100 border-base-300 w-full"
                /></td>
                <td>
                  <div className="flex gap-1 justify-end">
                    <button onClick={create}                 className="btn btn-ghost btn-xs text-success"><FaCheck /></button>
                    <button onClick={() => { setAdding(false); setDraft(EMPTY); }} className="btn btn-ghost btn-xs text-error"><FaTimes /></button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!adding && (
        <button onClick={() => setAdding(true)} className="btn btn-sm btn-ghost border border-base-300">
          <FaPlus /> {t("settings.presets.add")}
        </button>
      )}
    </div>
  );
}
